import React from 'react';
import { Camera, Instagram, Facebook, Youtube, Linkedin, Mail, Phone, MapPin, ArrowUp } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Portfolio', href: '#portfolio' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'Blog', href: '#blog' },
    { name: 'Contact', href: '#contact' },
  ];

  const services = [
    'Wedding Photography',
    'Portrait Sessions',
    'Event Coverage',
    'Product Photography',
    'Corporate Photography',
    'Pre-Wedding Shoots',
  ];

  const socialLinks = [
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Youtube, href: '#', label: 'YouTube' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-[#0a0a0a] border-t border-[#262626]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <a href="#home" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <Camera className="w-8 h-8 text-[#d4af37]" />
              <div>
                <span className="font-['Playfair_Display'] text-xl font-medium text-[#e5e5e5]">
                  NIK'S
                </span>
                <span className="text-[#a3a3a3] text-sm ml-2 tracking-widest uppercase hidden sm:inline">
                  Photography
                </span>
              </div>
            </a>
            <p className="text-[#a3a3a3] leading-relaxed mb-6">
              Capturing life's precious moments with cinematic elegance and timeless sophistication since 2014.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="p-2 bg-[#1a1a1a] border border-[#262626] hover:border-[#d4af37] hover:text-[#d4af37] text-[#a3a3a3] transition-all"
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg text-[#e5e5e5] mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-[#a3a3a3] hover:text-[#d4af37] transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg text-[#e5e5e5] mb-6">
              Our Services
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-[#a3a3a3] text-sm">
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg text-[#e5e5e5] mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" />
                <span className="text-[#a3a3a3] text-sm">
                  123 Photography Lane, Creative District, Mumbai 400001
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#d4af37] flex-shrink-0" />
                <a href="tel:+919876543210" className="text-[#a3a3a3] text-sm hover:text-[#d4af37] transition-colors">
                  +91 98765 43210
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#d4af37] flex-shrink-0" />
                <a href="mailto:hello@niksphotography.com" className="text-[#a3a3a3] text-sm hover:text-[#d4af37] transition-colors">
                  hello@niksphotography.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#a3a3a3] text-sm text-center md:text-left">
            © {currentYear} NIK'S Photography & Studio. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-[#a3a3a3] text-sm">
            <a href="#" className="hover:text-[#d4af37] transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-[#d4af37] transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button (for mobile) */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 p-3 bg-[#d4af37] text-[#0a0a0a] md:hidden z-40"
        aria-label="Scroll to top"
        data-testid="scroll-to-top-mobile"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </footer>
  );
};

export default Footer;
